import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { trafficQualityService, TrafficQualityQuery } from '../../services/statistics/traffic-quality';
import { useShopMap } from '../../services/store/organization.store';
import Dashboard from '../../components/Templates/Dashboard';
import TrendComboChart from '../../components/Charts/TrendComboChart';
import { getDefaultQuery, kpiFromStats } from './shop.effect';
import { StoreDiffRowEntity } from '../../types/base.dto';

const ShopComparePage: React.FC = () => {
  const { shopMap } = useShopMap();

  const fields: FieldConfig[] = [
    { 
      key: 'period', 
      label: '统计周期', 
      group: 'append', 
      required: true,
      type: 'select', 
      width: 75,
      options: [
        { label: '日', value: '日' },
        { label: '周', value: '周' },
        { label: '月', value: '月' },
      ],
      placeholder: '请选择',
      onValueChange: () => ({ orderCreatedAt: [] }) 
    },
    { 
      key: 'orderCreatedAt', 
      group: 'order', 
      label: '接单日期', 
      type: 'date', 
      required: true,
      operators: ['between'], 
      format: 'YYYY-MM-DD', 
      placeholder: ['开始日期', '结束日期'],
      disabledDate: (current: any, values: Record<string, any>, dates?: any[]) => {
        if (!current) return false;
        const period = values['period'] || '月';
        if (dates && dates.length > 0) {
           const date0 = dates[0] || dates[1];
           if (date0) {
             const diff = Math.abs(current.diff(date0, 'day'));
             if (period === '日' && diff > 31) return true;
             if (period === '周' && diff > 366) return true;
           }
        }
        return current > dayjs().endOf('day');
      }
    },
    { key: 'key', label: '对比指标', group: 'append', type: 'select', options: [
      // { label: '可发货订单数', value: 'shippedOrders' },
      // { label: '已发货订单数', value: 'receivedOrders' },
      { label: '风控通过订单数', value: 'riskPassOrders' },
      { label: '盲发订单数', value: 'blindShipOrders' },
      { label: '预收订单数', value: 'prePaidOrders' },
      { label: '高风险订单数', value: 'highRiskOrders' },
      { label: '全新苹果下单数', value: 'newAppleCount' },
      { label: '二手苹果下单数', value: 'secondAppleCount' },
      { label: '全新安卓下单数', value: 'newAndroidCount' },
    ] },
  ];

  const buildComboData = (rows: StoreDiffRowEntity[]) => {
    rows.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    const byDate = new Map<number, Record<string, number>>();
    const seriesKeys = new Set<string>();
    rows
      .forEach(r => {
        const ts = dayjs(r.date).valueOf();
        const shopIdNum = Number(r.store_id);
        const shop = shopMap.get(shopIdNum);
        const shopName = shop?.name || String(shopIdNum) || '未知店铺';
        const items = [
          { key: `${shopName}`, value: r.value || 0 },
        ];
        let rec = byDate.get(ts);
        if (!rec) { rec = { date: ts }; byDate.set(ts, rec); }
        items.forEach(({ key, value }) => { seriesKeys.add(key); rec![key] = value; });
      });
    const data = Array.from(byDate.values());
    const seriesCfg = {} as Record<string, { kind?: 'line' | 'column'; aggregate?: 'sum' | 'avg' } & Record<string, any>>;
    Array.from(seriesKeys).forEach(k => { seriesCfg[k] = { kind: 'line', aggregate: 'sum' }; });
    return { data, seriesCfg };
  };

  

  const defaultQ = useMemo(() => {
    const end = dayjs().endOf('day');
    const start = end.subtract(11, 'month').startOf('month');
    return {
      filter: {
        order: {
          orderCreatedAt: { operator: 'between', value: [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')] }
        },
        append: {
          period: { operator: 'eq', value: '月' },
          key: { operator: 'eq', value: 'riskPassOrders' }
        }
      }
    } as any;
  }, []);

  return (
    <Dashboard<StoreDiffRowEntity, TrafficQualityQuery, any>
      title={'店铺数据对比'}
      queryFields={fields}
      fetcher={async (params) => {
        const [rows, stats] = await Promise.all([
          trafficQualityService.getTrafficQualityCompare(params as any),
          trafficQualityService.getTrafficQualityTrendStats(params as any),
        ]);
        return { rows, stats } as any;
      }}
      defaultQuery={defaultQ}
      kpiItems={(stats) => kpiFromStats(stats)}
      sections={[
        {
          key: 'shop-compare-trend',
          title: '店铺对比趋势',
          render: ({ rows }) => {
            const { data, seriesCfg } = buildComboData(rows);
            return <TrendComboChart title="" data={data as any} seriesCfg={seriesCfg as any} />;
          },
        },
      ]}
    />
  );
};

export default ShopComparePage;
